import React, { createContext, useContext, useState, useEffect, useCallback, useReducer, useRef, ReactNode } from 'react';
import { useInRouterContext, useLocation, useNavigate } from 'react-router-dom';
import { DirectionProvider } from '@radix-ui/react-direction';

import type { Language } from '@/types/i18n';
import {
  changeI18nLanguage,
  resolveLegacy,
  resolveKey,
  resolvePlural,
  isEnabledLanguage,
  DEFAULT_LANGUAGE,
} from '@/lib/i18n';
import { getDirection } from '@/lib/i18n/direction';
import { localizedPath, urlLanguageOf } from '@/lib/localeRoutes';

type Direction = 'rtl' | 'ltr';

const STORAGE_KEY = 'dalali-language';

export interface LanguageContextType {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
  /**
   * Legacy bilingual resolver — the existing call style. Arabic & English are
   * returned inline; any other active language resolves the English string
   * through the generated catalog. Prefer `tk` / `tn` (keyed) in new code.
   */
  t: (ar: string, en: string) => string;
  /** Keyed lookup in the `app` namespace (no pluralization). */
  tk: (key: string, vars?: Record<string, unknown>) => string;
  /** Plural-aware keyed lookup (CLDR plural rules via i18next). */
  tn: (key: string, count: number, vars?: Record<string, unknown>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function readInitialLanguage(): Language {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;
  // localStorage can be null or throw on access in privacy modes and
  // restricted WebViews even though `window` exists; this runs synchronously
  // in the first render, so an unguarded read white-screens the app (#3769).
  try {
    const saved = window.localStorage?.getItem(STORAGE_KEY);
    if (saved && isEnabledLanguage(saved)) return saved;
  } catch {
    // Storage unavailable — boot with the default language.
  }
  return DEFAULT_LANGUAGE;
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [storedLanguage, setLanguageState] = useState<Language>(readInitialLanguage);
  const [, bump] = useReducer((n: number) => n + 1, 0);

  // On public marketing pages the URL decides the language: /en/... is
  // English, the root is Arabic (src/lib/localeRoutes.ts). A visitor who chose
  // one of the other locales keeps it at the root. Everywhere else the stored
  // preference applies. Computed during render, so the first paint (and the
  // build-time prerender) is already in the URL's language.
  // The app mounts this provider inside BrowserRouter; unit tests may mount it
  // bare. That never changes for a provider instance, so the conditional
  // router hooks below always run in the same order.
  const inRouter = useInRouterContext();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const location = inRouter ? useLocation() : null;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const navigate = inRouter ? useNavigate() : null;
  const urlLanguage = location ? urlLanguageOf(location.pathname) : null;
  const language: Language =
    urlLanguage === 'en'
      ? 'en'
      : urlLanguage === 'ar' && (storedLanguage === 'ar' || storedLanguage === 'en')
        ? 'ar'
        : storedLanguage;
  const routerRef = useRef({ location, navigate });
  routerRef.current = { location, navigate };

  // The engine is initialised eagerly at module load (see @/lib/i18n), so the
  // first render already resolves strings — no null gate, no blank tree
  // (critical for synchronous test renders and SSR).
  const direction = getDirection(language) as Direction;

  // If the initial language is a lazy locale (e.g. a persisted zh), fetch its
  // bundle then re-render so its strings replace the English fallback. The
  // eager primaries (ar/en) are already active from initI18n — skip them so we
  // don't trigger an extra render.
  useEffect(() => {
    if (storedLanguage === 'ar' || storedLanguage === 'en') return;
    let cancelled = false;
    // changeI18nLanguage already calls ensureLocaleLoaded internally (guarded
    // by loadedLocales), so one call both fetches the bundle and activates it.
    changeI18nLanguage(storedLanguage)
      .then(() => {
        if (cancelled) return;
        bump();
      })
      .catch((err) => console.error(`Failed to hydrate locale "${storedLanguage}":`, err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', language);
  }, [language, direction]);

  const setLanguage = useCallback((lang: Language) => {
    if (!isEnabledLanguage(lang)) return;
    // Load the locale bundle (lazy chunk) BEFORE flipping state so the new
    // strings are present on the first render in the new language. A failed
    // dynamic import (flaky CDN / stale chunk hash) must not leave the click
    // silently dead — keep the current language and surface the error.
    (async () => {
      try {
        await changeI18nLanguage(lang);
        setLanguageState(lang);
        window.localStorage?.setItem(STORAGE_KEY, lang);
        // On a marketing page the language lives in the URL: go to this
        // page's English (/en/...) or root version instead of swapping text.
        const { location: loc, navigate: nav } = routerRef.current;
        if (loc && nav && urlLanguageOf(loc.pathname)) {
          const target = localizedPath(loc.pathname, lang === 'en' ? 'en' : 'ar');
          if (target !== loc.pathname) nav(`${target}${loc.search}${loc.hash}`);
        }
      } catch (err) {
        console.error(`Failed to load locale "${lang}":`, err);
      }
    })();
  }, []);

  // Resolvers re-create when `language` changes so dependent memos/effects
  // recompute. Arabic/English are pure functions of their args; other locales
  // read the (already-loaded) i18next bundle.
  const t = useCallback(
    (ar: string, en: string) => resolveLegacy(language, ar, en),
    [language],
  );
  // `language` is intentionally a dep: when the locale switches, `tk`/`tn` must
  // get a fresh identity so memos/effects that depend on them recompute against
  // the newly-loaded bundle (the body reads the active language via i18next).
  const tk = useCallback(
    (key: string, vars?: Record<string, unknown>) => resolveKey(key, vars, language),
    [language],
  );
  const tn = useCallback(
    (key: string, count: number, vars?: Record<string, unknown>) =>
      resolvePlural(key, count, vars, language),
    [language],
  );

  return (
    <LanguageContext.Provider value={{ language, direction, setLanguage, t, tk, tn }}>
      <DirectionProvider dir={direction}>
        {children}
      </DirectionProvider>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
